import type { Response } from 'express';
import type { SeoMetaPayload } from '@sbt-web/network/types';
import { isBrowser } from '@sbt-web/utils';
import { ListingLinks } from '@/client/components/SEO/InternalLinks/types';
import { SearchState } from '@/reducers/search';
import { getListingLinks } from '@/shared/plexus-client';
import { registerSeoError } from '@/tools/errorHelpers';

// This is an inline helper for Plexus. It would be best to extend it
// but we need to validate the logic before canonicalizing it.
const hasSearchFilters = (searchState: SearchState): boolean => {
  return (
    searchState.qso ||
    searchState.includeShippableOnly ||
    searchState.includeUrgent ||
    !!searchState.query ||
    searchState.radiusSearch != undefined ||
    Object.keys(searchState.filters).length > 0
  );
};

export const getListingLinksResponseHandler = async (
  metiRequest: Promise<SeoMetaPayload>,
  searchState: SearchState,
  res?: Response,
  isBotifyUserAgent?: boolean,
  requestId?: string
): Promise<ListingLinks | null> => {
  return isBrowser()
    ? // When on the client, remove the internal linking content.
      // Plexus does not support CORS so the call would fail anyway.
      Promise.resolve<null>(null)
    : // Otherwise call Plexus
      // Get the key SEO data for the page from Meti
      // using the request already set up.
      metiRequest.then<ListingLinks | null>(async (metiSeoData) => {
        try {
          return await getListingLinks(
            {
              // Send the plain search query to Plexus
              baseQuery: searchState.query
                ? searchState.query
                    // Removing dashes
                    .replace(/-/g, '')
                    // Removing multiple spaces
                    .replace(/\s{2,}/g, ' ')
                    // Removing leading and trailing spaces
                    .trim()
                : '',
              pageUrl: metiSeoData.canonicalUrl,
              category: searchState.category,
              geo: searchState.geo,
              hasFilters: hasSearchFilters(searchState),
            },
            isBotifyUserAgent,
            requestId
          );
        } catch (e) {
          // If Plexus fails to respond, we should log the error and
          // return null to the client, which safely renders nothing.
          registerSeoError(res, 'PLEXUS', e);

          return null;
        }
      });
};
